// src/pages/PublicSamplePage.jsx
// Public page for sharing book samples on social media
// Auto-creates a guest session and redirects to player

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, BookOpen, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const PublicSamplePage = () => {
  const { manuscriptId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [bookInfo, setBookInfo] = useState(null);

  useEffect(() => {
    const createGuestSession = async () => {
      try {
        // Create guest sample session
        const response = await axios.post(`${API}/manuscripts/${manuscriptId}/sample/guest`);
        const { session_id, anonymous_id } = response.data;
        
        console.log('[PublicSample] Guest session created:', session_id);
        
        // Navigate to player with guest sample mode
        navigate(`/player/${session_id}`, {
          state: {
            guestSample: true,
            anonymousId: anonymous_id,
            returnTo: '/public/library'
          },
          replace: true // Replace history so back button doesn't loop
        });
      } catch (err) {
        console.error('[PublicSample] Failed to create session:', err);
        const errorMessage = err.response?.data?.detail || 'This sample is not available.';
        setError(errorMessage);
        
        // Try to get book info for error display
        try {
          const bookResponse = await axios.get(`${API}/public/manuscripts/${manuscriptId}`);
          setBookInfo(bookResponse.data);
        } catch {
          // Ignore - just show generic error
        }
      }
    };

    if (manuscriptId) {
      createGuestSession();
    }
  }, [manuscriptId, navigate]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Sample Not Available</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          
          {bookInfo && (
            <div className="mb-6 p-4 bg-slate-700/30 rounded-lg">
              <p className="text-sm text-slate-300">
                <strong>{bookInfo.title}</strong>
                {bookInfo.author && <span> by {bookInfo.author}</span>}
              </p>
            </div>
          )}
          
          <a 
            href="/public/library"
            className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-full transition-colors"
          >
            <BookOpen className="w-4 h-4" />
            Browse Library
          </a>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
        <p className="text-slate-400">Loading sample...</p>
      </div>
    </div>
  );
};

export default PublicSamplePage;
